import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "../lib/router";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { PageHeader } from "../components/PageHeader";
import { MaskedLines, Overline, Reveal } from "../components/motion";
import { PButton } from "../components/forms";
import { EditionSelector, ProductStage } from "../components/editions";
import { DEFAULT_EDITION, EDITIONS, REFORMERS, findEdition } from "../data/products";

const SpecRow = ({ label, value, testId }) => (
  <div className="flex justify-between gap-6 border-b border-[#1C1C1A]/10 pb-3">
    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#66625A]">{label}</dt>
    <dd data-testid={testId} className="text-right text-[#1C1C1A]">{value}</dd>
  </div>
);

export default function Reformer() {
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const [edition, setEdition] = useState(findEdition(searchParams.get("edition") || DEFAULT_EDITION).code);
  const ed = findEdition(edition);
  useMeta(t.seo.reformer[0], t.seo.reformer[1]);

  return (
    <div data-testid="reformer-page">
      <PageHeader id="reformer" overline={t.reformer.overline} lines={t.reformer.title} sub={`${t.reformer.sub} ${t.reformer.intro}`} />

      <section data-testid="reformer-editions" className="mx-auto max-w-[1400px] px-6 pb-28 md:px-12 md:pb-40 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal y={14}>
              <Overline className="mb-6">{t.reformer.editionsOverline}</Overline>
            </Reveal>
            <h2 data-testid="reformer-editions-title" className="font-serif text-4xl font-light leading-[0.98] tracking-tight text-[#1C1C1A] md:text-6xl">
              <MaskedLines lines={t.reformer.editionsTitle} />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md text-base font-light leading-relaxed text-[#66625A]">{t.reformer.editionsText}</p>
              <div className="mt-12">
                <EditionSelector value={edition} onChange={setEdition} size="lg" testPrefix="collection-edition" />
              </div>
              <p data-testid="reformer-active-edition-name" className="mt-8 font-serif text-2xl font-light text-[#1C1C1A]">{ed.label[lang]}</p>
              <p className="mt-3 text-sm font-light text-[#66625A]">{t.reformer.hint}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ProductStage product={REFORMERS[0]} edition={edition} testId="collection-stage" className="aspect-[4/3] p-8 md:p-14" imgClassName="drop-shadow-[0_30px_40px_rgba(0,0,0,0.18)]">
              <div className={`absolute bottom-6 left-6 z-[2] flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] md:bottom-8 md:left-8 ${ed.dark ? "text-[#FAF8F5]/80" : "text-[#66625A]"}`}>
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ed.swatch }} />
                <span data-testid="collection-active-edition">{t.reformer.shown}: {REFORMERS[0].shortName} · {ed.label[lang]}</span>
              </div>
            </ProductStage>
          </Reveal>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12 lg:px-20">
        <Reveal y={14}>
          <Overline className="mb-16 border-t border-[#1C1C1A]/10 pt-10">{t.reformer.modelsOverline}</Overline>
        </Reveal>
        <div className="space-y-28 md:space-y-40">
          {REFORMERS.map((p, i) => (
            <section key={p.slug} data-testid={`reformer-item-${p.slug}`} className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
              <Reveal className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
                <Link to={`/reformer/${p.slug}?edition=${edition}`} data-testid={`reformer-image-link-${p.slug}`} className="group block">
                  <ProductStage product={p} edition={edition} testId={`reformer-stage-${p.slug}`} className="aspect-[4/3] p-8 md:p-12" imgClassName="transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03] drop-shadow-[0_24px_36px_rgba(0,0,0,0.16)]" />
                </Link>
              </Reveal>

              <div className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.12}>
                  <Overline className="mb-4">{String(i + 1).padStart(2, "0")}</Overline>
                  <h2 data-testid={`reformer-name-${p.slug}`} className="mb-5 font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#1C1C1A] md:text-5xl">
                    {p.titleLines.map((l) => (
                      <span key={l} className="block">{l}</span>
                    ))}
                  </h2>
                  <p className="mb-8 max-w-md text-base font-light leading-relaxed text-[#66625A]">{p.desc[lang]}</p>
                  <dl className="mb-8 space-y-4 text-sm">
                    <SpecRow label={t.common.material} value={p.material[lang]} testId={`reformer-material-${p.slug}`} />
                    <SpecRow label={t.common.dims} value={p.dims} testId={`reformer-dims-${p.slug}`} />
                    <SpecRow label={t.common.finishesLabel} value={EDITIONS.map((e) => e.name).join(" · ")} testId={`reformer-editions-${p.slug}`} />
                  </dl>
                  <p data-testid={`reformer-price-${p.slug}`} className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">
                    {t.common.priceOnRequest}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <PButton to={`/reformer/${p.slug}?edition=${edition}`} variant="solid" data-testid={`reformer-details-${p.slug}`}>
                      {t.common.discover.replace("{x}", p.shortName)}
                    </PButton>
                    <PButton to={`/anfrage?produkt=${p.slug}&edition=${edition}`} variant="outline" data-testid={`reformer-quote-${p.slug}`}>
                      {t.common.quote}
                    </PButton>
                  </div>
                </Reveal>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
